import './UserList.css';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { HiOutlineMagnifyingGlass, HiOutlineArrowTopRightOnSquare, HiOutlinePlusCircle } from 'react-icons/hi2';
import { HiOutlineEllipsisVertical } from 'react-icons/hi2';
import { fetchUsers, UserData, downgradeUserRole, removeUser } from '../../services/userService';
import { useAuthStore } from '../../stores';
import InviteUserDialog from '../../components/InviteUserDialog/InviteUserDialog';
import toast from 'react-hot-toast';

type StatusFilter = 'All' | 'Active' | 'Inactive';

function UserList() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  const user = useAuthStore((state) => state.user);
  const currentUserRole = (user as any)?.role || 'bdo';

  // Get current user's role hierarchy from the fetched users list
  const currentUserHierarchy = useMemo(() => {
    const currentUser = users.find((u) => u.id === (user as any)?.id);
    return currentUser?.user_role?.role_hierarchy ?? (currentUserRole === 'mod' ? 1 : 2);
  }, [users, user, currentUserRole]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const data = await fetchUsers();
    setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const searchMatch =
        searchQuery === '' ||
        (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.department || '').toLowerCase().includes(searchQuery.toLowerCase());
      const statusMatch =
        statusFilter === 'All' ||
        (statusFilter === 'Active' && u.is_active) ||
        (statusFilter === 'Inactive' && !u.is_active);
      return searchMatch && statusMatch;
    });
  }, [users, searchQuery, statusFilter]);

  // Build a map of user IDs to names for resolving invited_by
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach((u) => {
      map[u.id] = u.name || u.email;
    });
    return map;
  }, [users]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuUserId(null);
      }
    };
    if (actionMenuUserId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuUserId]);

  const handleDowngrade = async (u: UserData) => {
    setActionMenuUserId(null);
    if (u.role !== 'mod') {
      toast.error('Cannot downgrade this role further');
      return;
    }
    const targetRole = 'BDO';
    if (!window.confirm(`Downgrade ${u.name || u.email} from ${u.role.toUpperCase()} to ${targetRole}?`)) return;
    const result = await downgradeUserRole(u.id, u.role);
    if (result.success) {
      toast.success(`${u.name || u.email} downgraded to ${targetRole}`);
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to downgrade user');
    }
  };

  const handleRemoveUser = async (u: UserData) => {
    setActionMenuUserId(null);
    if (!window.confirm(`Remove ${u.name || u.email} from the organization? This action cannot be undone.`)) return;
    const result = await removeUser(u.id);
    if (result.success) {
      toast.success(`${u.name || u.email} removed from organization`);
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to remove user');
    }
  };

  const totalCount = users.length;
  const modCount = users.filter((u) => u.role === 'mod').length;
  const bdoCount = users.filter((u) => u.role === 'bdo').length;
  const customerCount = users.filter((u) => u.role === 'customer').length;
  const activeCount = users.filter((u) => u.is_active).length;

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="user-list-page">
      <div className="user-list-header">
        <div className="user-list-title-row">
          <h1 className="user-list-title">User List</h1>
          <div className="user-list-summary">
            <span className="summary-chip">
              <span className="summary-chip-label">Total</span>
              <span className="summary-chip-value">{totalCount}</span>
            </span>
            {currentUserRole !== 'bdo' && (
              <span className="summary-chip chip-mod">
                <span className="summary-chip-label">Mod</span>
                <span className="summary-chip-value">{modCount}</span>
              </span>
            )}
            <span className="summary-chip chip-bdo">
              <span className="summary-chip-label">BDO</span>
              <span className="summary-chip-value">{bdoCount}</span>
            </span>
            <span className="summary-chip chip-customer">
              <span className="summary-chip-label">Customer</span>
              <span className="summary-chip-value">{customerCount}</span>
            </span>
            <span className="summary-chip chip-active">
              <span className="summary-chip-label">Active</span>
              <span className="summary-chip-value">{activeCount}</span>
            </span>
          </div>
        </div>

        <div className="user-list-controls">
          <div className="user-search-bar">
            <HiOutlineMagnifyingGlass className="user-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="user-search-input"
            />
          </div>
          <div className="user-filter-group">
            <div className="user-filter-pills">
              {(['All', 'Active', 'Inactive'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  className={`filter-pill ${statusFilter === status ? 'active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {status === 'All' ? 'All Status' : status}
                </button>
              ))}
            </div>
            <button className="invite-user-btn" onClick={() => setInviteDialogOpen(true)}>
              <HiOutlinePlusCircle />
              Invite User
            </button>
          </div>
        </div>
      </div>

      <div className="user-list-table-wrapper">
        <table className="user-list-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Role</th>
              <th>Department</th>
              <th>Title</th>
              <th>Invited By</th>
              <th>Status</th>
              <th>Joined At</th>
              <th>Activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="user-empty-state">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((u, idx) => (
                <tr key={u.id}>
                  <td className="user-row-num">{idx + 1}</td>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        {(u.name || u.email || '??').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <span className="user-name">{u.name || '-'}</span>
                        <span className="user-email">{u.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge role-${u.role}`}>
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td>{u.department || '-'}</td>
                  <td>{u.title || '-'}</td>
                  <td>{u.invited_by ? (userNameMap[u.invited_by] || '-') : '-'}</td>
                  <td>
                    <span className={`status-badge ${u.is_active ? 'status-active' : 'status-pending'}`}>
                      {u.is_active ? 'Active' : 'Pending'}
                    </span>
                  </td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>
                    <a
                      href={`https://us.posthog.com/project/309021/person/${u.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="posthog-link"
                      title="View usage activity"
                    >
                      Usage Activity <HiOutlineArrowTopRightOnSquare />
                    </a>
                  </td>
                  <td className="user-actions-cell">
                    {u.id !== (user as any)?.id && (
                      <div className="user-actions-wrapper" ref={actionMenuUserId === u.id ? actionMenuRef : undefined}>
                        <button
                          className="user-actions-btn"
                          onClick={() => setActionMenuUserId(actionMenuUserId === u.id ? null : u.id)}
                          title="Actions"
                        >
                          <HiOutlineEllipsisVertical />
                        </button>
                        {actionMenuUserId === u.id && (
                          <div className="user-actions-menu">
                            {u.role === 'mod' && (
                              <button
                                className="user-action-item"
                                onClick={() => handleDowngrade(u)}
                              >
                                Downgrade to BDO
                              </button>
                            )}
                            <button
                              className="user-action-item action-danger"
                              onClick={() => handleRemoveUser(u)}
                            >
                              Remove tool access
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="user-empty-state">
                  No users match the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <InviteUserDialog
        isOpen={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onUserInvited={loadUsers}
        currentUserRoleHierarchy={currentUserHierarchy}
      />
    </div>
  );
}

export default UserList;
